import { ViteReactSSG } from 'vite-react-ssg';
import { routes } from './routes';
import { setLanguageFromPath, loadLanguage } from './config/i18n';
import { installGlobalErrorHandlers } from './lib/errorReporter';
import { isChunkLoadError } from './lib/chunkLoadError';
import { initGA, grantAnalyticsConsent } from './lib/analytics';
import { initPixel, grantPixelConsent } from './lib/metaPixel';
import { hasConsentedToCookies } from './components/CookieConsent';
import './index.css';
import 'flag-icons/css/flag-icons.min.css';

// Install global error/rejection handlers (client-side only)
if (typeof window !== 'undefined') {
  installGlobalErrorHandlers();

  // Null-safe chunk-reload flag. sessionStorage is null / throws in private mode
  // or when storage is blocked, and the recovery handler must NOT itself throw
  // (digest 2026-06-28: "Cannot read properties of null (reading 'getItem')" on
  // public pages). In-memory fallback still guarantees exactly one reload.
  let chunkReloadMemFlag = false;
  const chunkReloadAlready = (): boolean => {
    if (chunkReloadMemFlag) return true;
    try { return sessionStorage.getItem('chunk-error-reload') === '1'; } catch { return false; }
  };
  const markChunkReload = (): void => {
    chunkReloadMemFlag = true;
    try { sessionStorage.setItem('chunk-error-reload', '1'); } catch { /* storage blocked */ }
  };

  // Chunk-load recovery. A lazy-loaded route module can fail to fetch when:
  //  - the user's session predates the latest deploy (old index.html still
  //    references chunk hashes that no longer exist on the server)
  //  - the user's network drops mid-request (common on mobile)
  //  - a CDN edge briefly 404s a fresh asset
  // Without recovery the user sees a blank screen or "Failed to fetch
  // dynamically imported module" toast. The fix: on first such failure,
  // hard-reload — the new index.html points at current chunks and the
  // navigation retries. Guarded by a session flag so we don't loop if the
  // failure is real (chunk genuinely missing). Reported pattern:
  // err_mpevvnn7_c1ftdt (designer chunk, mobile, 2026-05-21).
  const handleChunkError = (err: unknown) => {
    // Shared matcher (src/lib/chunkLoadError) — was Chrome/Safari-only, so
    // Firefox ("error loading dynamically imported module") and stale-export
    // ("Importing binding name") preload/import rejections escaped to a
    // white screen instead of the recovery reload.
    if (!isChunkLoadError(err)) {
      return false;
    }
    if (chunkReloadAlready()) return false; // already tried once this session; don't loop
    markChunkReload();
    window.location.reload();
    return true;
  };
  // Vite fires this for <link rel="modulepreload"> failures.
  window.addEventListener('vite:preloadError', (e) => {
    if (handleChunkError((e as unknown as CustomEvent).detail || e)) e.preventDefault();
  });
  // Runtime import() rejections that escape route Suspense boundaries.
  window.addEventListener('unhandledrejection', (e) => {
    if (handleChunkError(e.reason)) e.preventDefault();
  });

  // A chunk whose body is HTML (a 200 interstitial/error page cached under a
  // JS URL by a stale service worker — see public/sw.js) parses as a module
  // and throws SyntaxError "Unexpected token '<'". That surfaces as a window
  // 'error', not a rejection, so handleChunkError above never sees it, and a
  // plain reload would re-serve the poisoned chunk. Purge the SW caches and
  // unregister, then reload once, so the browser refetches fresh chunks.
  window.addEventListener('error', (e) => {
    const msg = String((e as ErrorEvent).message || '');
    if (!/Unexpected token '?<'?/.test(msg)) return;
    if (chunkReloadAlready()) return;
    markChunkReload();
    void (async () => {
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(
            keys.filter((k) => k.startsWith('cctvplanner-')).map((k) => caches.delete(k)),
          );
        }
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
      } catch {
        /* best effort — reload anyway */
      }
      window.location.reload();
    })();
  });

  // GA4 + Meta Pixel init. Both default to consent-denied so the loaders run
  // (allowing instant fire after grant) but no cookies/IDs are set until the
  // user accepts the cookie banner. PageView fires from <RouteTracker> on
  // every route change for both providers.
  initGA();
  initPixel();
  // Restore granted consent from a prior session (localStorage).
  if (hasConsentedToCookies()) {
    grantAnalyticsConsent();
    grantPixelConsent();
  }

  // Register service worker for static asset caching (production only).
  // Vendor chunks (cameras-db 1.4MB, vendor-three 996KB, vendor-pdf 889KB)
  // become instant on second visit. SW versioning is in /sw.js — bumped per release.
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    window.addEventListener('load', () => {
      // updateViaCache:'none' makes the browser bypass the HTTP cache when
      // checking sw.js for updates — defense in depth against a CDN/host that
      // pins the SW script (Cloudflare served a 21-day-old sw.js as immutable;
      // see public/.htaccess), so a new SW is picked up on the next visit.
      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {
        // Silent fail — SW is progressive enhancement, not critical.
      });
    });

    // When a new SW with skipWaiting + clients.claim takes over mid-session,
    // the active page is still wired to the old asset URLs. Without a hard
    // reload we get a vendor-chunk mismatch the moment the user triggers any
    // dynamic import (Stripe Checkout, PDF export, three.js V3 designer).
    // First-time SW registration also fires this event — use the
    // `hasController` flag to skip that case so the very first visit doesn't
    // self-reload.
    let hasController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hasController) {
        window.location.reload();
      } else {
        hasController = true;
      }
    });
  }
}

// Extract language from a path (same logic as setLanguageFromPath but returns the lang)
function extractLang(pathname: string): string {
  const LANG_PREFIXES = ['pl', 'de', 'es', 'fil', 'hi', 'id', 'pt-br', 'pt-pt', 'tr', 'ar', 'vi', 'th', 'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl', 'hu', 'lt', 'ro', 'sv'];
  const segments = pathname.split('/').filter(Boolean);
  return (segments[0] && LANG_PREFIXES.includes(segments[0])) ? segments[0] : 'en';
}

export const createRoot = ViteReactSSG(
  {
    routes,
    // vite-react-ssg defaults v7_startTransition to TRUE, which wraps every
    // router navigation in React.startTransition. React then keeps the OLD
    // page mounted (no Suspense fallback) while a lazy route chunk
    // downloads — so clicking a CTA into the designer (~MBs on slow mobile)
    // showed zero feedback for 10s+ and the button felt dead. Opting out
    // commits the navigation immediately, letting RootLayout's branded
    // Suspense fallback paint for the duration of the chunk fetch.
    future: { v7_startTransition: false },
  },
  async (context) => {
    // Determine which language this page needs
    const pathname = context.routePath || (context.isClient ? window.location.pathname : '/');
    const lang = extractLang(pathname);

    // Load language resources (async, works in both SSG/Node and client)
    // English is always available; non-English languages are loaded on demand.
    if (lang !== 'en') {
      await loadLanguage(lang);
    }

    // Set the language synchronously on the i18n instance (resources are now available)
    setLanguageFromPath(pathname);
  },
);
